import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

export default function MultipleChoiceCard({ question, accentWord, options, selected, correctAnswer, onAnswer, current, total, label, questionIndex = 0 }) {
    const pct = total > 0 ? (current / total) * 100 : 0;
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const bg = isDark
        ? 'linear-gradient(135deg, #0d0d1a 0%, #12122a 40%, #0a1628 100%)'
        : 'linear-gradient(135deg, #dddaf0 0%, #e0ddf2 40%, #d8e0f0 100%)';
    const glow1 = isDark ? 'rgba(108,99,255,0.1)' : 'rgba(108,99,255,0.06)';
    const progressBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(80,70,140,0.1)';
    const topbarBg = isDark ? 'rgba(13,13,26,0.85)' : 'rgba(220,218,240,0.92)';
    const topbarBorder = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(80,70,140,0.12)';
    const labelColor = isDark ? '#7878a0' : '#5050a0';
    const counterColor = isDark ? '#e8e8f0' : '#1a1830';
    const cardBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.55)';
    const cardBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(80,70,140,0.15)';
    const questionColor = isDark ? '#e8e8f0' : '#1a1830';

    let questionNode;
    if (accentWord && typeof question === 'string') {
        const parts = question.split(accentWord);
        questionNode = (<>{parts[0]}<span style={{ background: 'linear-gradient(135deg, #6c63ff, #2575fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', fontWeight: 800 }}>{accentWord}</span>{parts[1]}</>);
    } else {
        questionNode = question;
    }

    const getOptionStyle = (opt) => {
        if (selected === null) {
            return {
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.5)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(80,70,140,0.2)'}`,
                color: isDark ? '#e8e8f0' : '#1a1830',
                '&:hover': {
                    background: isDark ? 'rgba(108,99,255,0.15)' : 'rgba(85,72,224,0.12)',
                    border: `1px solid ${isDark ? 'rgba(108,99,255,0.5)' : 'rgba(85,72,224,0.45)'}`,
                    transform: 'translateY(-1px)',
                },
            };
        }
        if (opt === correctAnswer) return { background: 'rgba(74,222,128,0.15)', border: '1px solid rgba(74,222,128,0.5)', color: '#4ade80' };
        if (opt === selected) return { background: 'rgba(248,113,113,0.15)', border: '1px solid rgba(248,113,113,0.5)', color: '#f87171' };
        return {
            background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.25)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(80,70,140,0.08)'}`,
            color: isDark ? '#7878a0' : '#8080b0',
        };
    };

    return (
        <Box sx={{
            minHeight: '100vh', width: '100%', background: bg,
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            pt: { xs: 8, sm: 10 }, pb: 6, px: { xs: 2, sm: 3 },
            position: 'relative', overflow: 'hidden',
            '&::before': { content: '""', position: 'fixed', top: '-20%', right: '-10%', width: '60vw', height: '60vw', borderRadius: '50%', background: `radial-gradient(circle, ${glow1} 0%, transparent 70%)`, pointerEvents: 'none', zIndex: 0 },
        }}>
            <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 }}>
                <Box sx={{ height: 3, background: progressBg, position: 'relative' }}>
                    <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: 'linear-gradient(90deg, #6c63ff, #2575fc)', transition: 'width 0.4s ease', borderRadius: '0 2px 2px 0' }} />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 2, sm: 4 }, py: 1, background: topbarBg, backdropFilter: 'blur(12px)', borderBottom: `1px solid ${topbarBorder}` }}>
                    <Typography sx={{ color: labelColor, fontSize: '0.78rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'DM Sans', sans-serif" }}>{label}</Typography>
                    <Typography sx={{ color: counterColor, fontSize: '0.85rem', fontWeight: 700, fontFamily: "'Syne', sans-serif" }}>
                        {current} <span style={{ color: labelColor, fontWeight: 400 }}>/ {total}</span>
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ width: '100%', maxWidth: 520, position: 'relative', zIndex: 1 }}>
                <AnimatePresence mode="wait">
                    <motion.div key={questionIndex} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}>
                        <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: '20px', p: { xs: 3, sm: 4 }, mb: 2, textAlign: 'center' }}>
                            <Typography sx={{ color: questionColor, fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: { xs: '1.35rem', sm: '1.6rem' }, lineHeight: 1.3 }}>
                                {questionNode}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {options.map((opt, idx) => (
                                <motion.div key={idx} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.07, duration: 0.25 }}>
                                    <Button fullWidth variant="outlined" onClick={() => selected === null && onAnswer(opt)} sx={{
                                        borderRadius: '14px', py: 1.8, fontSize: '1rem', fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
                                        textTransform: 'none', transition: 'all 0.2s ease', cursor: selected !== null ? 'default' : 'pointer',
                                        ...getOptionStyle(opt),
                                    }}>
                                        {opt}
                                    </Button>
                                </motion.div>
                            ))}
                        </Box>
                    </motion.div>
                </AnimatePresence>
            </Box>
        </Box>
    );
}
